import { doc, onSnapshot } from 'firebase/firestore';
import { ADMIN_ENDPOINTS } from '@/config/endpoints';
import { auth, db } from '@/lib/firebase';
import {
  MAINTENANCE_HUB_IDS,
  OFF_MAINTENANCE_STATE,
  parseMaintenanceData,
  type MaintenanceHubId,
  type MaintenanceState,
  type MaintenanceUpdate,
} from './maintenanceState';

export { OFF_MAINTENANCE_STATE, parseMaintenanceData } from './maintenanceState';
export type { MaintenanceActor, MaintenanceState, MaintenanceUpdate } from './maintenanceState';

export const MAINTENANCE_DOCUMENT_PATH = 'system_config/maintenance';

export function subscribeToMaintenance(
  onState: (state: MaintenanceState) => void,
  onError: (error: Error) => void,
): () => void {
  return onSnapshot(
    doc(db, 'system_config', 'maintenance'),
    (snapshot) => {
      if (!snapshot.exists()) {
        onState(OFF_MAINTENANCE_STATE);
        return;
      }
      try {
        onState(parseMaintenanceData(snapshot.data()));
      } catch (error) {
        onError(error instanceof Error ? error : new Error(String(error)));
      }
    },
    (error) => onError(error),
  );
}

async function adminRequest<T>(method: 'GET' | 'PATCH', body?: MaintenanceUpdate, path: string = ADMIN_ENDPOINTS.maintenance): Promise<T> {
  const currentUser = auth.currentUser;
  if (!currentUser) throw new Error('Autenticazione richiesta');
  const token = await currentUser.getIdToken();
  const response = await fetch(path, {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    ...(body ? { body: JSON.stringify({ data: body }) } : {}),
  });
  const text = await response.text();
  let payload: { result?: T; error?: string } = {};
  try {
    payload = text ? JSON.parse(text) : {};
  } catch {
    payload = {};
  }
  if (!response.ok) {
    throw new Error(payload.error || `HTTP ${response.status}`);
  }
  return payload.result as T;
}

export async function getAdminMaintenanceState(): Promise<MaintenanceState> {
  return adminRequest<MaintenanceState>('GET');
}

export async function updateAdminMaintenanceState(update: MaintenanceUpdate): Promise<MaintenanceState> {
  return adminRequest<MaintenanceState>('PATCH', update);
}

export async function updateAdminMaintenanceGlobal(update: MaintenanceUpdate): Promise<MaintenanceState> {
  return adminRequest<MaintenanceState>('PATCH', update, `${ADMIN_ENDPOINTS.maintenance}/global`);
}

export async function updateAdminMaintenanceHub(
  hubId: MaintenanceHubId,
  update: MaintenanceUpdate,
): Promise<MaintenanceState> {
  if (!MAINTENANCE_HUB_IDS.includes(hubId)) throw new Error('Hub manutenzione non valido');
  return adminRequest<MaintenanceState>('PATCH', update, `${ADMIN_ENDPOINTS.maintenance}/hubs/${encodeURIComponent(hubId)}`);
}
